import React, { useState, useEffect } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableHead, TableRow, 
  Chip, Button, CircularProgress, Alert, TextField, TablePagination, Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api';

const PayslipReport = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, msg: '', type: 'success' });
  
  // Pagination & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPayslips = async () => {
    setLoading(true);
    try {
      const res = await api.get('/payroll/payslips/report');
      setPayslips(res.data || []);
    } catch (err) {
      showNotif('Failed to load payslip report.', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPayslips(); }, []);

  const showNotif = (msg, type = 'success') => {
    setNotification({ show: true, msg, type });
    setTimeout(() => setNotification({ show: false, msg: '', type: 'success' }), 5000);
  };

  const handleDownload = async (id, filename) => {
    try {
      const res = await api.get(`/payroll/payslips/download/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { showNotif('Failed to download PDF.', 'error'); }
  };

  const filteredPayslips = payslips.filter(p => 
    p.emp_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    p.emp_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedPayslips = filteredPayslips.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {notification.show && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.msg}</Alert>}
      
      <TextField 
        size="small" label="Search Name, ID or Department" 
        value={searchQuery} onChange={(e) => {setSearchQuery(e.target.value); setPage(0);}} 
        sx={{ width: 300, mb: 3 }} 
      />

      {loading ? <CircularProgress /> : (
        <>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell><b>Month</b></TableCell>
                <TableCell><b>Generated On</b></TableCell>
                <TableCell><b>Employee Details</b></TableCell>
                <TableCell><b>Dept & Designation</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell align="center"><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPayslips.map(p => (
                <TableRow key={p.id}>
                  <TableCell><b>{p.month} {p.year}</b></TableCell>
                  <TableCell>{new Date(p.generated_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{p.emp_name}</Typography>
                    <Typography variant="caption" color="textSecondary">{p.emp_id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{p.department}</Typography>
                    <Typography variant="caption" color="textSecondary">{p.designation}</Typography>
                  </TableCell>
                  <TableCell><Chip label={p.status} size="small" color="success" /></TableCell>
                  <TableCell align="center">
                    <Button startIcon={<DownloadIcon />} size="small" variant="outlined" onClick={() => handleDownload(p.id, p.file_name)}>
                      Download PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {payslips.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>No approved payslips found.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={filteredPayslips.length} page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </Box>
  );
};

export default PayslipReport;